// src/hooks/useTasksQuery.ts
import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../lib/supabase";
import type { ID, Task } from "../types/domain";

export type UseTasksFilters = {
  projectId?: ID;
  search?: string;
  status?: Task["status"];
  orderBy?: keyof Task | "id" | "deadline" | "position";
  ascending?: boolean;
  limit?: number;
};

type SetFiltersArg =
  | Partial<UseTasksFilters>
  | ((prev: UseTasksFilters) => Partial<UseTasksFilters>);

export function useTasksQuery(initial: UseTasksFilters) {
  const [filters, setFiltersState] = useState<UseTasksFilters>(initial);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const setFilters = useCallback((arg: SetFiltersArg) => {
    setFiltersState((prev) => ({ ...prev, ...(typeof arg === "function" ? (arg as any)(prev) : arg) }));
  }, []);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      let q = supabase.from("tasks").select("*");

      if (filters.projectId) q = q.eq("project_id", filters.projectId);
      if (filters.status) q = q.eq("status", filters.status);

      const s = (filters.search ?? "").trim();
      if (s) q = q.or(`title.ilike.%${s}%,description.ilike.%${s}%`);

      const orderBy = (filters.orderBy ?? "id") as string;

      // Orden especial para tablero: status + position + id
      if (orderBy === "position") {
        q = q.order("status", { ascending: true })
             .order("position", { ascending: true, nullsFirst: true })
             .order("id", { ascending: true });
      } else {
        q = q.order(orderBy, { ascending: filters.ascending ?? true });
      }

      const { data, error } = await q;
      if (error) throw error;

      setTasks((data as Task[]) ?? []);
    } catch (e: any) {
      // Fallback si alguna columna no existiera
      setError(e?.message ?? "Error cargando tareas");
      const { data } = await supabase.from("tasks").select("*").order("id", { ascending: true });
      setTasks((data as Task[]) ?? []);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { void fetchTasks(); }, [fetchTasks]);

  const reload = useCallback(async () => { await fetchTasks(); }, [fetchTasks]);

  return useMemo(
    () => ({ tasks, loading, error, setFilters, reload }),
    [tasks, loading, error, setFilters, reload]
  );
}
